/**
 * Role Detail Page
 */
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  Typography,
  Divider,
  Alert,
  useTheme,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  People as PeopleIcon,
  CheckCircle as CheckIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { PageHeader } from '../../components/common';
import { useGetRoleQuery } from '../../store/api/rbacApi';
import { palette } from '../../theme/palette';
import {
  stripRetiredPermissionCodes,
  filterRetiredPermissionsFromList,
} from '../../utils/rbacRetiredPermissions';
import moment from 'moment';

const RoleDetailPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const { id } = useParams<{ id: string }>();
  const isDark = theme.palette.mode === 'dark';

  const { data, isLoading, error } = useGetRoleQuery(parseInt(id || '0'), {
    skip: !id,
  });

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data?.data) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', py: 10 }}>
        <Typography color="error">{t('rbac.roles.detail.notFound')}</Typography>
        <Button onClick={() => navigate('/roles')} sx={{ mt: 2 }}>
          {t('rbac.roles.backToList')}
        </Button>
      </Box>
    );
  }

  const role = data.data;
  const directPermissionsView = filterRetiredPermissionsFromList(role.permissions);
  const allPermissionsView = stripRetiredPermissionCodes(role.all_permissions);

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={t('rbac.roles.detail.title', { code: role.code })}
        breadcrumbs={[
          { label: t('nav.dashboard'), path: '/' },
          { label: t('nav.roles'), path: '/roles' },
          { label: role.name },
        ]}
      />

      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/roles')}
        sx={{ mb: 3 }}
      >
        {t('rbac.roles.backToList')}
      </Button>

      {/* System Role Warning */}
      {role.is_system && (
        <Alert severity="info" icon={<LockIcon />} sx={{ mb: 3 }}>
          {t('rbac.roles.detail.systemWarning')}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: '2fr 1fr',
          },
          gap: 3,
        }}
      >
        {/* Main Content */}
        <Box>
          <Card>
            <CardContent>
              {/* Header with Status */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight={600}>
                  {t('rbac.roles.detail.detailsHeading')}
                </Typography>
                <Chip
                  label={role.is_active ? t('rbac.status.active') : t('rbac.status.inactive')}
                  sx={{
                    backgroundColor: role.is_active
                      ? palette.status.success + '20'
                      : '#E2E8F0',
                    color: role.is_active
                      ? palette.status.success
                      : '#64748B',
                    fontWeight: 600,
                  }}
                />
              </Box>

              {/* Basic Information */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                  },
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('rbac.roles.detail.name')}</Typography>
                  <Typography variant="body1" fontWeight={600}>{role.name}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('rbac.roles.detail.code')}</Typography>
                  <Typography variant="body1">{role.code}</Typography>
                </Box>
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <Typography variant="caption" color="text.secondary">{t('rbac.roles.detail.description')}</Typography>
                  <Typography variant="body1">{role.description || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('rbac.roles.detail.type')}</Typography>
                  <Typography variant="body1">
                    <Chip
                      label={role.is_system ? t('rbac.roles.detail.systemRole') : t('rbac.roles.detail.customRole')}
                      size="small"
                      icon={role.is_system ? <LockIcon /> : undefined}
                      sx={{
                        backgroundColor: role.is_system
                          ? palette.brand[50]
                          : palette.brandSecondary[50],
                        color: role.is_system
                          ? palette.brand[700]
                          : palette.brandSecondary[700],
                        fontWeight: 600,
                        mt: 0.5,
                      }}
                    />
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('rbac.roles.detail.displayOrder')}</Typography>
                  <Typography variant="body1">{role.display_order}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('rbac.roles.detail.createdAt')}</Typography>
                  <Typography variant="body1">
                    {moment(role.created_at).format('MMMM DD, YYYY HH:mm')}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('rbac.roles.detail.lastUpdated')}</Typography>
                  <Typography variant="body1">
                    {moment(role.updated_at).format('MMMM DD, YYYY HH:mm')}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Assigned Permissions */}
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('rbac.roles.detail.assignedPermissions', { count: directPermissionsView.length })}
              </Typography>
              {directPermissionsView.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {directPermissionsView.map((permission) => (
                    <Chip
                      key={permission.id}
                      icon={<CheckIcon />}
                      label={permission.name}
                      size="small"
                      sx={{
                        backgroundColor: isDark ? palette.brand[800] : palette.brand[50],
                        color: isDark ? palette.brand[100] : palette.brand[700],
                        '& .MuiChip-icon': {
                          color: isDark ? palette.brand[200] : palette.brand[600],
                        },
                        transition: 'all 0.2s ease',
                        cursor: 'default',
                        '&:hover': {
                          backgroundColor: isDark ? palette.brand[700] : palette.brand[100],
                          transform: 'translateY(-2px)',
                          boxShadow: isDark
                            ? '0 4px 12px rgba(59, 193, 207, 0.4)'
                            : '0 2px 8px rgba(59, 193, 207, 0.3)',
                        },
                      }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('rbac.roles.detail.noPermissions')}
                </Typography>
              )}

              {/* All Permissions (including inherited) */}
              {allPermissionsView.length > directPermissionsView.length && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {t('rbac.roles.detail.allPermissions', { count: allPermissionsView.length })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('rbac.roles.detail.inheritedNote')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                    {allPermissionsView.map((permCode, index) => (
                      <Chip
                        key={index}
                        label={permCode}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: isDark ? palette.brand[600] : palette.brand[200],
                          color: isDark ? palette.brand[200] : palette.brand[600],
                          transition: 'all 0.2s ease',
                          cursor: 'default',
                          '&:hover': {
                            borderColor: isDark ? palette.brand[400] : palette.brand[400],
                            backgroundColor: isDark
                              ? palette.brand[800] + '60'
                              : palette.brand[50] + '40',
                            color: isDark ? palette.brand[100] : palette.brand[700],
                            transform: 'translateY(-2px)',
                            boxShadow: isDark
                              ? '0 4px 12px rgba(59, 193, 207, 0.3)'
                              : '0 2px 8px rgba(59, 193, 207, 0.2)',
                          },
                        }}
                      />
                    ))}
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Sidebar */}
        <Box>
          {/* Actions Card */}
          {!role.is_system && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {t('rbac.roles.detail.actions')}
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={() => navigate(`/roles/${id}/edit`)}
                >
                  {t('rbac.roles.detail.editRole')}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Role Summary Card */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('rbac.roles.detail.summary')}
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {role.name}
              </Typography>
              {role.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {role.description}
                </Typography>
              )}
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PeopleIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {role.employee_count === 1
                    ? t('rbac.roles.detail.employeeAssignedOne', { count: role.employee_count })
                    : t('rbac.roles.detail.employeeAssignedOther', { count: role.employee_count })}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t('rbac.roles.detail.directPermissions', { count: directPermissionsView.length })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('rbac.roles.detail.totalPermissions', { count: allPermissionsView.length })}
              </Typography>
              {role.is_system && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LockIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {t('rbac.roles.detail.protectedRole')}
                    </Typography>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>

          {/* Parent Role Card (if applicable) */}
          {role.parent_role && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {t('rbac.roles.detail.parentRole')}
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {role.parent_role.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {t('rbac.roles.detail.inheritsFromParent')}
                </Typography>
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  onClick={() => navigate(`/roles/${role.parent_role?.id}`)}
                  sx={{ mt: 2 }}
                >
                  {t('rbac.roles.detail.viewParentRole')}
                </Button>
              </CardContent>
            </Card>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default RoleDetailPage;
